(ns hai.home.calendar-card
  "Shared meeting row and 'Connect your calendar' advert for the home dashboard's
   Calendar mini-app. Required by both the desktop and mobile Calendar cards so
   the meeting row is one implementation. State and accessors come from
   [[hai.home.calendar]]; each surface owns its own card frame, header, and
   section layout."
  (:require
   [clojure.string :as str]
   [hai.datetime :as datetime]
   [hai.home.calendar :as calendar]
   [hai.home.clock :as clock]
   [hai.journal2.integration-registry :as reg]
   [hai.journal2.timeline :as tl]
   [hai.library.connections.invalid-reason :as invalid-reason]
   [hai.ui.components :as c]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

;; Live-meeting pulse for the status dot. Garden keyframes are evaluated at macro
;; time, so garden.stylesheet needs no cljs require here.
#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules now-pulse
  (garden.stylesheet/at-keyframes
   :calendar-now-pulse
   [:from {:box-shadow "0 0 0 0 rgba(71,5,208,0.45)"}]
   ["70%" {:box-shadow "0 0 0 8px rgba(71,5,208,0)"}]
   [:to {:box-shadow "0 0 0 0 rgba(71,5,208,0)"}]))

(o/defstyled meeting-row :div
  {:display "grid"
   :grid-template-columns "auto 10px 1fr auto"
   :gap "14px" :align-items "start"
   :padding "12px 16px"
   :background t/--card-surface
   :border-radius "12px"
   :border-width "1px" :border-style "solid" :border-color t/--card-border
   :transition "border-color 0.15s ease, box-shadow 0.15s ease"}
  [:&.clickable {:cursor "pointer"}]
  ;; Direct hover and the flow-card cross-hover share one lifted, ringed look.
  [#{:&.clickable:hover :&.highlighted}
   {:border-color t/--card-violet
    :box-shadow "0 2px 8px rgba(27,17,71,0.10)"}]
  [:.time-col
   {:display "flex" :flex-direction "column" :gap "2px"}]
  [:.time-label
   {:font-size "15px" :font-weight "700" :color t/--card-ink
    :font-variant-numeric "tabular-nums" :line-height "1.2"}]
  [:.duration
   {:font-size "12px" :font-weight "500" :color t/--card-text-muted :line-height "1.2"}]
  [:.dot
   {:width "10px" :height "10px" :border-radius "50%"
    :background t/--card-violet :flex-shrink "0"
    :margin-top "5px"}
   [:&.now
    {:background t/--humble-primary
     :box-shadow "0 0 0 0 rgba(71,5,208,0.45)"
     :animation "calendar-now-pulse 2s infinite"}]]
  [:.meeting-content
   {:min-width "0"
    :display "flex" :flex-direction "column" :gap "6px"}]
  [:.title-line
   {:display "flex" :flex-wrap "wrap" :align-items "center" :gap "8px"}]
  [:.meeting-title
   {:font-size "15px" :font-weight "600" :color t/--card-ink
    :line-height "1.3" :min-width "0"
    :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}]
  [:.countdown
   {:display "inline-flex" :align-items "center"
    :padding "3px 10px" :border-radius "999px"
    :font-size "11px" :font-weight "700" :white-space "nowrap"
    :background t/--card-violet-soft :color t/--card-violet}]
  [:.meeting-meta
   {:display "flex" :align-items "center" :gap "10px" :flex-wrap "wrap"}]
  [:.meet-label
   {:font-size "11px" :font-weight "500" :color t/--card-text-muted :white-space "nowrap"}]
  ;; Upcoming meetings get a soft pink fill — lighter than the header, the way
  ;; notes pairs its header/body yellows. Done (past) meetings stay unfilled.
  [#{:&.now :&.next :&.upcoming} {:background t/--calendar-light}]
  [:&.past
   [:.dot {:background t/--card-text-faint}]
   [:.time-label {:color t/--card-text-muted}]
   [:.meeting-title
    {:text-decoration "line-through" :color t/--card-text-muted :font-weight "500"}]])

(o/defstyled meeting-list :div
  {:display "flex" :flex-direction "column" :gap "12px"
   :padding "16px"})

(o/defstyled join-btn :button
  {:align-self "center"
   :border "none" :border-radius "999px"
   :padding "6px 12px"
   :font-family "inherit" :font-size "11px" :font-weight "700"
   :white-space "nowrap" :cursor "pointer"
   :background t/--humble-primary :color "white"
   :transition "background 0.15s ease, border-color 0.15s ease, color 0.15s ease"}
  [:&:hover {:background t/--humble-primary-dark}]
  ;; Ghost variant for the upcoming (:next) meeting — quieter than the live one.
  [:&.ghost
   {:background "transparent" :color t/--card-violet :font-weight "600"
    :border-width "1px" :border-style "solid" :border-color t/--card-border}
   [:&:hover {:background "transparent" :border-color t/--card-violet}]])

;; Attendee avatars — soft, harmonized fills so several people read as distinct
;; without clashing with the card's violet accents.
(def ^:private avatar-colors
  ["#6E5BCC" "#A183FF" "#C77DA6" "#7E9BD6" "#B98AD1" "#D69A7E"])

;; Dot colors for concurrent (overlapping) meetings, so they can be told apart.
(def ^:private lane-colors
  ["#6E5BCC" "#4705D0" "#A183FF" "#FEBBCC" "#7E9BD6" "#D69A7E"])

(o/defstyled avatar :div
  {:width "22px" :height "22px" :border-radius "50%"
   :border-width "2px" :border-style "solid" :border-color t/--card-surface
   :display "flex" :align-items "center" :justify-content "center"
   :font-size "9px" :font-weight "700" :color "white"
   :flex-shrink "0" :user-select "none"}
  [:&.overflow {:background t/--card-track :color t/--card-text-muted}])

(o/defstyled attendees :div
  {:display "flex" :align-items "center"}
  [:.avatar-slot {:display "inline-flex"}])

(defn- attendees-row [emails names]
  (let [max-show 4
        overflow (- (count emails) max-show)]
    [attendees
     (map-indexed
      (fn [index email]
        (let [display (or (get names index) (first (str/split email #"@")))
              color   (nth avatar-colors (mod index (count avatar-colors)))]
          ^{:key email}
          [c/Tooltip {:content display}
           [:span.avatar-slot {:style {:margin-left (when (pos? index) "-6px")}}
            [avatar {:style {:background color}} (tl/name->initials display)]]]))
      (take max-show emails))
     (when (pos? overflow)
       [c/Tooltip {:content (str "+" overflow " more")}
        [:span.avatar-slot {:style {:margin-left "-6px"}}
         [avatar {:class "overflow"} (str "+" overflow)]]])]))

(defn- minutes-from-now [inst now-ms]
  (when (and inst now-ms)
    (js/Math.round (/ (- (.getTime (js/Date. inst)) now-ms) 60000))))

(defn- countdown-label
  "Pill text for a meeting: time left for a live one, time until a future one."
  [status start end-at now-ms]
  (cond
    (= status :now)
    (let [remaining (minutes-from-now end-at now-ms)]
      (when (and remaining (pos? remaining))
        (str remaining " min left")))

    (contains? #{:next :upcoming} status)
    (let [mins (minutes-from-now start now-ms)]
      (when (and mins (pos? mins))
        (if (>= mins 60)
          (str "in " (quot mins 60) "h " (mod mins 60) "m")
          (str "in " mins "m"))))))

(defn- meeting-row-view
  "One meeting row. `:cross-highlight?` wires the hover exchange with the
   desktop flow card — the touch surface leaves it off, since a tap's synthetic
   mouseenter never gets a matching mouseleave and the highlight would stick."
  [meeting {:keys [cross-highlight?]}]
  (let [status     (::calendar/status meeting)
        uuid       (:journal.entry/uuid meeting)
        start      (:journal.entry/time meeting)
        end-at     (calendar/m-end-at meeting)
        link       (calendar/m-link meeting)
        meet-link  (calendar/m-meet-link meeting)
        meet-label (calendar/m-meet-label meeting)
        emails     (or (calendar/m-attendees meeting) [])
        names      (or (calendar/m-attendee-names meeting) [])
        duration   (tl/format-compact-duration start end-at)
        now-ms     @(<s [::clock/now-ms])
        pill       (countdown-label status start end-at now-ms)
        joinable?  (and meet-link (contains? #{:now :next} status))
        lane-info  (get @(<s [::calendar/meeting-layout]) uuid)
        hovered?   (and cross-highlight?
                        @(<s [::calendar/meeting-hovered? uuid]))]
    [meeting-row
     (cond-> {:class [(name (or status :upcoming))
                      (when link "clickable")
                      (when hovered? "highlighted")]}
       ;; Cross-highlight with the flow card: hovering here lights the
       ;; matching block over there, and vice-versa.
       cross-highlight? (assoc :on-mouse-enter #(>e [::calendar/hover-meeting uuid])
                               :on-mouse-leave #(>e [::calendar/hover-meeting nil]))
       link (assoc :on-click #(>e [:routing/navigate-in-new-tab link])))
     [:div.time-col
      [:div.time-label (datetime/format-time start)]
      (when duration [:div.duration duration])]
     [:div.dot
      {:class (when (= :now status) "now")
       ;; Concurrent meetings get a distinct lane color so they're tellable apart.
       :style (when (and (not= :now status) (:overlapping? lane-info))
                {:background (nth lane-colors (mod (or (:lane lane-info) 0) (count lane-colors)))
                 :opacity "0.8"})}]
     [:div.meeting-content
      [:div.title-line
       [:div.meeting-title (calendar/m-title meeting)]
       (when pill [:span.countdown pill])]
      (when (or (seq emails) meet-label)
        [:div.meeting-meta
         (when (seq emails) [attendees-row emails names])
         (when meet-label [:span.meet-label meet-label])])]
     (when joinable?
       [join-btn
        {:class (when (not= :now status) "ghost")
         :on-click (fn [e]
                     (.stopPropagation e)
                     (js/window.open meet-link "_blank" "noopener,noreferrer"))}
        (if (= :now status) "Join →" "Join")])]))

(o/defstyled advert :div
  {:position "relative"}
  [:.sample
   {:opacity "0.5" :pointer-events "none" :user-select "none"
    :display "flex" :flex-direction "column" :gap "12px"
    :padding "16px"}]
  [:.overlay
   {:position "absolute" :inset "0"
    :display "flex" :align-items "center" :justify-content "center"
    :padding "20px"
    :background "rgba(247,246,239,0.35)"}]
  [:.overlay-card
   {:display "flex" :flex-direction "column"
    :align-items "center" :text-align "center" :gap "8px"
    :max-width "300px"
    :padding "22px 24px"
    :border-radius "16px"
    :background t/--card-surface
    :box-shadow "0 2px 4px rgba(45,62,42,0.06), 0 10px 30px rgba(45,62,42,0.18)"}]
  [:.headline
   {:font-size "16px" :font-weight "700" :color t/--card-ink}]
  [:.sub
   {:font-size "13px" :color t/--card-text-muted :line-height "1.5"}]
  [:.connect-btn
   {:margin-top "4px"
    :background t/--humble-primary :color "white"
    :border "none" :border-radius "999px"
    :padding "9px 18px"
    :font-family "inherit" :font-size "13px" :font-weight "700"
    :cursor "pointer" :transition "background 0.15s"}
   [:&:hover {:background t/--humble-primary-dark}]])

(defn connect-advert
  "Sample meetings behind a 'Connect your calendar' overlay, shown when no
   calendar is linked."
  []
  [advert
   (into [:div.sample {:aria-hidden true}]
         (map (fn [meeting]
                ^{:key (:journal.entry/uuid meeting)}
                [meeting-row-view meeting nil]))
         (calendar/sample-meetings))
   [:div.overlay
    [:div.overlay-card
     [:div.headline "Your day, at a glance"]
     [:div.sub "Connect your calendar and today's meetings show up here automatically."]
     [:button.connect-btn
      {:type "button"
       :on-click #(>e [:timeline/open-calendar-picker])}
      "Connect your calendar"]]]])

(defn meeting-list-view
  "A padded list of meeting rows — the body of a card's meetings section.
   `opts` is forwarded to each row (see [[meeting-row-view]])."
  [meetings opts]
  (into [meeting-list]
        (map (fn [meeting]
               ^{:key (:journal.entry/uuid meeting)}
               [meeting-row-view meeting opts]))
        meetings))

;; ---------------------------------------------------------------------------
;; Broken-connection banner (multi-calendar: a card can have one healthy
;; calendar AND one broken one at the same time, so this renders alongside
;; the meetings list rather than replacing it like `connect-advert` does)
;; ---------------------------------------------------------------------------

(o/defstyled broken-banner :div
  {:display "flex" :align-items "center" :gap "8px"
   :margin "0 16px 12px" :padding "8px 12px"
   :border-radius "10px"
   :background t/--card-error-12
   :color t/--card-error
   :font-size "12px" :font-weight "500"}
  [:.text {:flex "1"}]
  [:a
   {:color "inherit" :font-weight "700" :text-decoration "underline"
    :white-space "nowrap"}])

(defn- broken-connection-notice
  "One broken calendar connection: what's wrong with it, plus a Reconnect link
  when reconnecting can fix it (see [[invalid-reason/describe]])."
  [{:keys [integration-type email invalid-reason]}]
  (let [{:keys [explanation reconnect?]} (invalid-reason/describe invalid-reason)]
    [broken-banner
     [:span.text
      (reg/integration-type-label integration-type)
      (when email (str " (" email ")"))
      ": " explanation]
     (when reconnect?
       [:a {:href (reg/oauth-url integration-type nil) :target "_top"}
        "Reconnect"])]))

(defn broken-connections-banner
  "One row per broken calendar connection — 'Google Calendar (email): <what's
   wrong>', with a Reconnect link when it can help. Reauth healing on the
   backend means following that link repairs THIS connection in place (same
   uuid/TI)."
  []
  (let [broken @(<s [::calendar/broken-connections])]
    (when (seq broken)
      (into [:<>]
            (map (fn [{:keys [integration-type email] :as connection}]
                   ^{:key (str integration-type email)}
                   [broken-connection-notice connection]))
            broken))))
